import React, { useState } from 'react';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-green-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_numerounosounds/artifacts/g7fudt7e_137643.jpg" 
              alt="Numerounosounds" 
              className="h-16 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-gray-300 hover:text-green-400 transition-colors duration-300 font-medium"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('artists')}
              className="text-gray-300 hover:text-green-400 transition-colors duration-300 font-medium"
            >
              Artists
            </button>
            <button 
              onClick={() => scrollToSection('dubplates')}
              className="text-gray-300 hover:text-green-400 transition-colors duration-300 font-medium"
            >
              Custom Dubplates
            </button>
            <button 
              onClick={() => scrollToSection('releases')}
              className="text-gray-300 hover:text-green-400 transition-colors duration-300 font-medium"
            >
              Releases
            </button>
            
            {/* VIP SUBSCRIBE BUTTON - YELLOW TEXT, MORE VISIBLE */}
            <Link 
              to="/vip"
              className="relative px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-yellow-400 rounded-lg font-bold text-base hover:from-red-500 hover:to-red-600 transition-all duration-300 shadow-[0_0_25px_rgba(220,38,38,0.7)] hover:shadow-[0_0_40px_rgba(220,38,38,1)] animate-pulse-glow"
              style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
            >
              🔥 SUBSCRIBE 🔥
            </Link>
            
            <button 
              onClick={() => scrollToSection('demo')}
              className="px-6 py-2 bg-green-500/10 border border-green-500 text-green-400 rounded-lg hover:bg-green-500 hover:text-black transition-all duration-300 font-semibold shadow-[0_0_15px_rgba(0,255,136,0.3)] hover:shadow-[0_0_25px_rgba(0,255,136,0.6)]"
            >
              Submit Demo
            </button>

            {/* User Menu */}
            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-sm">
                  {user.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-red-400 transition-colors flex items-center gap-2"
                  title="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-gray-300 hover:text-green-400 transition-colors flex items-center gap-2"
              >
                <User size={20} />
                <span>Sign In</span>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-green-400 hover:text-green-300 transition-colors"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-green-500/20">
          <div className="px-4 pt-4 pb-6 space-y-4">
            <button 
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-gray-300 hover:text-green-400 transition-colors py-2 font-medium"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('artists')}
              className="block w-full text-left text-gray-300 hover:text-green-400 transition-colors py-2 font-medium"
            >
              Artists
            </button>
            <button 
              onClick={() => scrollToSection('dubplates')}
              className="block w-full text-left text-gray-300 hover:text-green-400 transition-colors py-2 font-medium"
            >
              Custom Dubplates
            </button>
            <button 
              onClick={() => scrollToSection('releases')}
              className="block w-full text-left text-gray-300 hover:text-green-400 transition-colors py-2 font-medium"
            >
              Releases
            </button>
            
            {/* VIP SUBSCRIBE BUTTON - MOBILE - YELLOW TEXT */}
            <Link 
              to="/vip"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center px-6 py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-yellow-400 rounded-lg font-bold text-lg hover:from-red-500 hover:to-red-600 transition-all duration-300 shadow-[0_0_30px_rgba(220,38,38,0.7)] animate-pulse-glow"
              style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
            >
              🔥 SUBSCRIBE NOW 🔥
            </Link>
            
            <button 
              onClick={() => scrollToSection('demo')}
              className="block w-full text-left px-6 py-3 bg-green-500/10 border border-green-500 text-green-400 rounded-lg hover:bg-green-500 hover:text-black transition-all duration-300 font-semibold"
            >
              Submit Demo
            </button>

            {/* User Menu - Mobile */}
            {user ? (
              <>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-gray-400 text-sm mb-2">Signed in as:</p>
                  <p className="text-white font-medium">{user.name}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-6 py-3 bg-red-500/10 border border-red-500 text-red-400 rounded-lg hover:bg-red-500 hover:text-white transition-all duration-300 font-semibold"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="block w-full text-center px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all duration-300 font-semibold"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
